import SecurityAPI from '../../api/security'
import axios from 'axios'
import { useStorage } from '@vueuse/core'

const tokenStorage = useStorage('lernleitstand-store-token', '')
const userStorage = useStorage('lernleitstand-store-user', {})
const isAuthenticatedStorage = useStorage('lernleitstand-store-isAuthenticated', false)
const originUserStorage = useStorage('origin-user', {})
const switchUserStorage = useStorage('switch-user', '')

const switchUser = switchUserStorage.value
const token = tokenStorage.value
let user = userStorage.value
user = Object.keys(user).length === 0 ? false : user
let isAuthenticated = isAuthenticatedStorage.value

if (token && token.length > 10) {
    axios.defaults.headers.common = { Authorization: `Bearer ${token}` }
    if (switchUser) {
        axios.defaults.headers.common['-SWITCH-USER'] = switchUser
    }
} else {
    tokenStorage.value = ''
}

if (!user || !token) {
    isAuthenticated = false
}

const AUTHENTICATING = 'AUTHENTICATING',
    AUTHENTICATING_SUCCESS = 'AUTHENTICATING_SUCCESS',
    AUTHENTICATING_ERROR = 'AUTHENTICATING_ERROR',
    IS_CONFIRMATION_TOKEN_VALID = 'IS_CONFIRMATION_TOKEN_VALID',
    IS_CONFIRMATION_TOKEN_VALID_SUCCESS = 'IS_CONFIRMATION_TOKEN_VALID_SUCCESS',
    IS_CONFIRMATION_TOKEN_VALID_ERROR = 'IS_CONFIRMATION_TOKEN_VALID_ERROR',
    REQUEST_PASSWORD_RESET = 'REQUEST_PASSWORD_RESET',
    REQUEST_PASSWORD_RESET_SUCCESS = 'REQUEST_PASSWORD_RESET_SUCCESS',
    REQUEST_PASSWORD_RESET_ERROR = 'REQUEST_PASSWORD_RESET_ERROR',
    CHANGE_PASSWORD = 'CHANGE_PASSWORD',
    CHANGE_PASSWORD_SUCCESS = 'CHANGE_PASSWORD_SUCCESS',
    CHANGE_PASSWORD_ERROR = 'CHANGE_PASSWORD_ERROR',
    PROVIDING_DATA_ON_REFRESH_SUCCESS = 'PROVIDING_DATA_ON_REFRESH_SUCCESS'

export default {
    namespaced: true,
    state: {
        isLoading: false,
        error: null,
        isAuthenticated: isAuthenticated,
        user: user,
        confirmationTokenError: null,
        passwordChangeError: null
    },
    getters: {
        isLoading(state) {
            return state.isLoading
        },
        isUserSwitched() {
            return !!switchUserStorage.value
        },
        hasError(state) {
            return state.error !== null
        },
        error(state) {
            return state.error
        },
        confirmationTokenError(state) {
            return state.confirmationTokenError
        },
        hasConfirmationTokenError(state) {
            return state.confirmationTokenError !== null
        },
        passwordChangeError(state) {
            return state.passwordChangeError
        },
        hasPasswordChangeError(state) {
            return state.passwordChangeError !== null
        },
        isAuthenticated(state) {
            return state.isAuthenticated
        },
        isAdmin(state, getters) {
            return getters.hasRolle('Admin')
        },
        isStudienfachberater(state, getters) {
            return getters.hasRolle('Studienfachberater(in)')
        },
        isStudienDekan(state, getters) {
            return getters.hasRolle('Studiendekan')
        },
        isModulVerantwortlicher(state, getters) {
            return getters.hasRolle('Modulverantwortliche(r)')
        },
        isLehrender(state, getters) {
            return getters.hasRolle('Lehrende(r)')
        },
        isHochschulleitung(state, getters) {
            return getters.hasRolle('Hochschulleitung')
        },
        isDekan(state, getters) {
            return getters.hasRolle('Dekan')
        },
        isQMB(state, getters) {
            return getters.hasRolle('Qualitätsmanagement Beauftragte(r)')
        },
        isGast(state, getters) {
            return getters.hasRolle('Gast')
        },
        // Spiegelt App\Application\Security\KennzahlenAccessResolver:
        // Read-all → Admin / Hochschulleitung / QMB(isForAll); Scope → Dekan und
        // Studiendekan (fachbereichIds) oder SFB / QMB ohne isForAll (studiengangIds);
        // sonst keine Sicht.
        canViewKennwerte(state, getters) {
            if (!state.user || !state.user.rollen) {
                return false
            }
            if (getters.isAdmin || getters.isHochschulleitung) {
                return true
            }
            const qmb = state.user.rollen['Qualitätsmanagement Beauftragte(r)']
            if (qmb) {
                if (qmb.isForAll) {
                    return true
                }
                if (Array.isArray(qmb.studiengangIds) && qmb.studiengangIds.length > 0) {
                    return true
                }
            }
            const dekan = state.user.rollen['Dekan']
            if (dekan && (dekan.isForAll || (Array.isArray(dekan.fachbereichIds) && dekan.fachbereichIds.length > 0))) {
                return true
            }
            const sd = state.user.rollen['Studiendekan']
            if (sd && (sd.isForAll || (Array.isArray(sd.fachbereichIds) && sd.fachbereichIds.length > 0))) {
                return true
            }
            const sfb = state.user.rollen['Studienfachberater(in)']
            if (sfb && (sfb.isForAll || (Array.isArray(sfb.studiengangIds) && sfb.studiengangIds.length > 0))) {
                return true
            }
            return false
        },
        isManager(state, getters) {
            return getters.hasRolle('Manager_in')
        },
        hasBefragungAuthorisation(state, getters) {
            return getters.hasRolle('Admin') || getters.hasRolle('Assistent_in')
        },
        hasRequestRevisionRole(state, getters) {
            return getters.hasRolle('Admin') || getters.hasRolle('Manager_in')
        },
        currentUser(state) {
            return state.user
        },
        hasRolle(state) {
            return (rolle) => {
                if (!state.user) {
                    return false
                }

                return state.user.rollen.hasOwnProperty(rolle)
            }
        }
    },
    mutations: {
        [AUTHENTICATING](state) {
            state.isLoading = true
            state.error = null
            state.isAuthenticated = false
            state.user = null
        },
        [AUTHENTICATING_SUCCESS](state, payload) {
            state.isLoading = false
            state.error = null
            state.isAuthenticated = true
            state.user = payload.user
            tokenStorage.value = payload.token
            userStorage.value = payload.user
            isAuthenticatedStorage.value = true
            axios.defaults.headers.common = { Authorization: 'Bearer ' + payload.token }
        },
        [AUTHENTICATING_ERROR](state, error) {
            state.isLoading = false
            state.error = error
            state.isAuthenticated = false
            state.user = null
            axios.defaults.headers.common = {}
        },
        [CHANGE_PASSWORD](state) {
            state.isLoading = true
            state.error = null
            state.passwordChangeError = null
        },
        [CHANGE_PASSWORD_SUCCESS](state) {
            state.isLoading = false
            state.error = null
            state.passwordChangeError = null
        },
        [CHANGE_PASSWORD_ERROR](state, error) {
            state.isLoading = false
            state.error = error
            state.passwordChangeError = error.data
        },
        [IS_CONFIRMATION_TOKEN_VALID](state) {
            state.isLoading = true
            state.error = null
            state.confirmationTokenError = null
        },
        [IS_CONFIRMATION_TOKEN_VALID_SUCCESS](state) {
            state.isLoading = false
            state.error = null
            state.confirmationTokenError = null
        },
        [IS_CONFIRMATION_TOKEN_VALID_ERROR](state, error) {
            state.isLoading = false
            state.error = error
            state.confirmationTokenError = error.data
        },
        [REQUEST_PASSWORD_RESET](state) {
            state.isLoading = true
            state.error = null
        },
        [REQUEST_PASSWORD_RESET_SUCCESS](state) {
            state.isLoading = false
            state.error = null
        },
        [REQUEST_PASSWORD_RESET_ERROR](state, error) {
            state.isLoading = false
            state.error = error
        },
        [PROVIDING_DATA_ON_REFRESH_SUCCESS](state, payload) {
            state.isLoading = false
            state.error = null
            state.confirmationTokenError = null
            state.passwordChangeError = null
            state.isAuthenticated = payload.isAuthenticated
            state.user = payload.user
            tokenStorage.value = payload.token
            userStorage.value = payload.user
            isAuthenticatedStorage.value = payload.isAuthenticated
            if (payload.token) {
                axios.defaults.headers.common = { Authorization: 'Bearer ' + payload.token }
            } else {
                axios.defaults.headers.common = {}
                switchUserStorage.value = ''
                originUserStorage.value = {}
            }
        }
    },
    actions: {
        async login({ commit }, payload) {
            commit(AUTHENTICATING)
            try {
                const response = await SecurityAPI.login(payload.username, payload.password)
                axios.defaults.headers.common = { Authorization: 'Bearer ' + response.data.token }
                const userResponse = await SecurityAPI.find(response.data['@id'])
                response.data.user = userResponse.data
                await commit(AUTHENTICATING_SUCCESS, response.data)
                return response.data
            } catch (error) {
                commit(AUTHENTICATING_ERROR, error)
                return null
            }
        },
        onRefresh({ commit }, payload) {
            commit(PROVIDING_DATA_ON_REFRESH_SUCCESS, payload)
        },
        async isConfirmationTokenValid({ commit }, { userId, confirmationToken }) {
            commit(IS_CONFIRMATION_TOKEN_VALID)
            try {
                let response = await SecurityAPI.isConfirmationTokenValid(userId, confirmationToken)
                commit(IS_CONFIRMATION_TOKEN_VALID_SUCCESS)
                return response.data
            } catch (error) {
                commit(IS_CONFIRMATION_TOKEN_VALID_ERROR, error)
                return null
            }
        },
        async changePassword({ commit }, { userId, password, confirmationToken }) {
            commit(CHANGE_PASSWORD)
            try {
                let response = await SecurityAPI.changePassword(userId, password, confirmationToken)
                commit(CHANGE_PASSWORD_SUCCESS)
                return response.data
            } catch (error) {
                commit(CHANGE_PASSWORD_ERROR, error)
                return null
            }
        },
        async requestPasswordReset({ commit }, { email, honeypot }) {
            commit(REQUEST_PASSWORD_RESET)
            try {
                let response = await SecurityAPI.requestPasswordReset(email, honeypot)
                commit(REQUEST_PASSWORD_RESET_SUCCESS)
                return response.data
            } catch (error) {
                commit(REQUEST_PASSWORD_RESET_ERROR, error)
                return null
            }
        },
        async switchUser(unused, user) {
            const switchUser = await SecurityAPI.find(`/api/security-users/${user.userId}`)
            switchUserStorage.value = user.email.email
            originUserStorage.value = userStorage.value
            userStorage.value = switchUser.data
            window.location.reload()
        },
        async exitSwitchUser() {
            userStorage.value = originUserStorage.value
            switchUserStorage.value = ''
            originUserStorage.value = {}
            window.location.reload()
        }
    }
}
